import { z } from "zod";
import { MetaSchema } from "./shared";
import {
  TransactionTypeEnum,
  PlatformMainServicesEnum,
  LoanTargetsEnum,
  LoanPaymentEnum,
} from "../enums";
import { TransactionEnum } from "../enums/TransactionTypeEnum";

// --------------------
// Invest Transaction
// --------------------

export const CacheTransactionInvestPlanSchema = z.object({
  id: z.string().uuid(),
  count: z.union([z.string(), z.number()]).optional().nullable(),
  price: z.union([z.string(), z.number()]).optional().nullable(),
});

export const CacheTransactionInvestBodySchema = z.object({
  amount: z.number(),
  value: z.number(),
  coin: z.string(),
  type: z.string(),
  investPlans: z.array(CacheTransactionInvestPlanSchema),
  includeChineseBonus: z.boolean().optional().nullable(),
  transactionType: TransactionTypeEnum.transactionSchema,
  platform: PlatformMainServicesEnum.platformMainServicesSchema,
  refCode: z.string().optional(),
  useProfit: z.number().nullable().optional(),
  useGGold: z.number().nullable().optional(),
  hash: z.string().optional().nullable(),
});

// --------------------
// Loan Transaction
// --------------------

export const CacheTransactionLoanSchema = z.object({
  amount: z.number().optional(),
  collateral: z.number().optional(),
  purpose: LoanTargetsEnum.loanTargetsSchema.optional(),
  targetLtv: z.number().optional(),
  loanId: z.string().uuid().optional(),
  userLoanId: z.string().optional(),
});

export const CacheTransactionLoanBodySchema = z.object({
  amount: z.number(),
  value: z.number().optional(),
  paymentType: LoanPaymentEnum.loanPaymentSchema.optional(),
  coin: z.string().optional(),
  type: z.string().optional(),
  hash: z.string().optional().nullable(),
  platform: z
    .literal(PlatformMainServicesEnum.PlatformMainServicesEnum.LOAN)
    .optional(),
  loan: CacheTransactionLoanSchema.optional(),
  feeToGr: z.number().nullable().optional(),
  purpose: LoanTargetsEnum.loanTargetsSchema.optional(),
  useProfit: z.number().nullable().optional(),
  useGGold: z.number().nullable().optional(),
  transactionType: z.literal(TransactionEnum.DEPOSIT).nullable().optional(),
  includeChineseBonus: z.boolean().optional().nullable(),
});

// --------------------
// Trade Transaction
// --------------------

export const CacheTransactionTradeBodySchema = z.object({
  amount: z.number(),
  value: z.number(),
  coin: z.string(),
  type: z.string(),
  transactionType: z.literal(TransactionEnum.TRADEDEPOSIT),
  platform: z.literal(PlatformMainServicesEnum.PlatformMainServicesEnum.TRADE),
  useProfit: z.number().nullable().optional(),
  useGGold: z.number().nullable().optional(),
  hash: z.string().optional().nullable(),
  includeChineseBonus: z.boolean().optional().nullable(),
});

// --------------------
// Request Body
// --------------------

export const CacheTransactionBodySchema = z.union([
  CacheTransactionInvestBodySchema,
  CacheTransactionLoanBodySchema,
  CacheTransactionTradeBodySchema,
]);

// --------------------
// Response
// --------------------

const WalletsCacheTransactionResponse = z.object({
  data: z.string(),
  meta: MetaSchema.default,
});

export default WalletsCacheTransactionResponse;

// --------------------
// Types
// --------------------

export type CacheTransactionBodyType = z.infer<
  typeof CacheTransactionBodySchema
>;

export type CacheTransactionInvestBodyType = z.infer<
  typeof CacheTransactionInvestBodySchema
>;

export type CacheTransactionLoanBodyType = z.infer<
  typeof CacheTransactionLoanBodySchema
>;

export type CacheTransactionTradeBodyType = z.infer<
  typeof CacheTransactionTradeBodySchema
>;

export type WalletsCacheTransactionResponseType = z.infer<
  typeof WalletsCacheTransactionResponse
>;
